/**
 * Browser-side selfie validation + embedding via face-api.js.
 *
 * Returns a structured verdict so onboarding can show user-friendly errors
 * and store a quality_score alongside the embedding.
 */
import * as faceapi from "face-api.js";

const MODEL_URL = "https://justadudewhohacks.github.io/face-api.js/models";
export const MODEL_VERSION = "face-api.js@0.22.2/face_recognition_net";
export const GEOMETRY_VERSION = "geom-v1.0";
export const GEOMETRY_DIM = 22;

let loadPromise = null;

export async function loadFaceModels() {
  if (!loadPromise) {
    loadPromise = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]).catch((e) => {
      loadPromise = null;
      throw e;
    });
  }
  return loadPromise;
}

export function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve({ img, url });
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

const FRIENDLY_REASONS = {
  no_face: "We couldn't find a face. Try a brighter, front-facing selfie.",
  multiple_faces: "We saw more than one face. Solo selfies only.",
  tiny_face: "Move closer — your face is too small in this photo.",
  low_confidence: "The angle is too extreme. Face the camera.",
  too_dark: "It's too dark. Find better light and try again.",
  too_small_image: "This image is too small. Use a higher-resolution photo.",
  decode_failed: "We couldn't read that image. Try a JPG or PNG.",
};

function reasonText(code) {
  return FRIENDLY_REASONS[code] || "We couldn't process that photo. Pick another.";
}

// ── Landmark geometry ────────────────────────────────────────────────────
// Compute a scale/rotation-invariant geometry vector from face-api.js's
// 68-point landmark set (iBUG 300W layout). The goal: capture the BONES of
// the face — proportion, spacing, jaw/cheekbone/chin shape — and ignore
// styling like hair, makeup, expression and lighting. Doppel matching then
// uses this in addition to the embedding so two people with the same hair
// don't match while two genuinely similar faces with different hair do.
//
// Index reference (iBUG 300W):
//   jawline       0..16   (0 = right ear, 8 = chin tip, 16 = left ear)
//   right brow    17..21
//   left brow     22..26
//   nose bridge   27..30  (27 = nasion / between-eyes, 30 = nose tip)
//   nose base     31..35  (31..35 across the nostrils)
//   right eye     36..41
//   left eye      42..47
//   outer mouth   48..59  (48 = right corner, 54 = left corner, 51 = cupid, 57 = lower lip mid)
//   inner mouth   60..67

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const centroid = (pts) => {
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: x / pts.length, y: y / pts.length };
};
// Rotate point around origin by angle (radians). Used to align faces to
// horizontal eye line before measuring proportions.
const rotate = (p, c, ang) => {
  const cos = Math.cos(ang), sin = Math.sin(ang);
  const dx = p.x - c.x, dy = p.y - c.y;
  return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
};

/**
 * Compute a ~22-D normalized geometry vector from face-api.js landmarks.
 *
 * Pipeline:
 *  1. Get all 68 positions.
 *  2. Align so eye line is horizontal (rotation invariance).
 *  3. Compute inter-ocular distance (IOD) as our scale unit.
 *  4. Build features as ratios — all scale/translation/rotation invariant.
 *
 * Returns null if landmarks look degenerate.
 */
export function computeGeometry(landmarks) {
  if (!landmarks?.positions || landmarks.positions.length < 68) return null;
  const raw = landmarks.positions;
  // Convert to plain {x,y} so we don't mutate face-api's Point objects.
  let pts = raw.map((p) => ({ x: p.x, y: p.y }));

  // Eye centers from each eye's 6 landmarks.
  const rightEye = pts.slice(36, 42);
  const leftEye = pts.slice(42, 48);
  const rEyeC = centroid(rightEye);
  const lEyeC = centroid(leftEye);
  // Align: rotate so the eye-line is horizontal.
  const angle = Math.atan2(lEyeC.y - rEyeC.y, lEyeC.x - rEyeC.x);
  const center = mid(rEyeC, lEyeC);
  pts = pts.map((p) => rotate(p, center, -angle));
  // Recompute eye centers in the aligned frame so all downstream math is
  // consistent (we use the aligned coordinates everywhere below).
  const rEye = centroid(pts.slice(36, 42));
  const lEye = centroid(pts.slice(42, 48));
  const iod = dist(rEye, lEye);
  if (!isFinite(iod) || iod < 1) return null;

  // Convenience landmarks.
  const noseTip = pts[30];
  const nasion = pts[27]; // between brows
  const subnasale = mid(pts[31], pts[35]); // bottom of the nose (alar base)
  const noseLeft = pts[31], noseRight = pts[35]; // narrow nostril width
  const noseOuterL = pts[35], noseOuterR = pts[31]; // alae width
  const mouthR = pts[48], mouthL = pts[54];
  const upperLipTop = pts[51], lowerLipBottom = pts[57];
  const stomion = mid(pts[62], pts[66]); // closed-mouth midpoint
  const chin = pts[8];
  const jawL = pts[16], jawR = pts[0];
  const gonionL = pts[12], gonionR = pts[4]; // approximate angle of jaw
  const cheekL = pts[15], cheekR = pts[1]; // cheekbone width proxy
  const browR = centroid(pts.slice(17, 22));
  const browL = centroid(pts.slice(22, 27));
  const browMid = mid(browR, browL);
  const eyeMid = mid(rEye, lEye);

  // Face width = horizontal span at the cheekbones; face height = nasion → chin
  const faceWidth = Math.abs(cheekL.x - cheekR.x) || iod * 2.5;
  const faceHeight = dist(nasion, chin) || iod * 2.0;
  // Eye widths (corner-corner, aligned axis)
  const rEyeWidth = dist(pts[36], pts[39]);
  const lEyeWidth = dist(pts[42], pts[45]);
  const rEyeHeight = (dist(pts[37], pts[41]) + dist(pts[38], pts[40])) / 2;
  const lEyeHeight = (dist(pts[43], pts[47]) + dist(pts[44], pts[46])) / 2;

  // Symmetry: average mirrored-pair distance after centering on noseTip x.
  // Closer to 0 = more symmetric.
  const cx = noseTip.x;
  const mirroredPairs = [
    [0, 16], [1, 15], [2, 14], [3, 13], [4, 12], [5, 11], [6, 10], [7, 9], // jaw
    [17, 26], [18, 25], [19, 24], [20, 23], [21, 22], // brows
    [36, 45], [39, 42], [31, 35], [48, 54], // eyes / nose alae / mouth corners
  ];
  let sym = 0;
  for (const [a, b] of mirroredPairs) {
    const A = pts[a], B = pts[b];
    // mirror B across cx and measure how far it lands from A
    sym += Math.hypot((A.x - cx) - (cx - B.x), A.y - B.y);
  }
  const symScore = sym / (mirroredPairs.length * iod);

  // Jaw angle (slope of jawline from gonion → chin) — narrow vs square jaw.
  const jawSlopeR = Math.atan2(chin.y - gonionR.y, chin.x - gonionR.x);
  const jawSlopeL = Math.atan2(chin.y - gonionL.y, gonionL.x - chin.x);
  const jawAngleAvg = (jawSlopeR + jawSlopeL) / 2; // radians

  // Final feature vector — all dimensionless ratios so different image sizes
  // / different distances from the camera produce comparable vectors.
  const feats = [
    iod / faceWidth,                                        // eye spacing share of face width
    dist(noseLeft, noseRight) / faceWidth,                  // nose width / face width
    dist(noseOuterL, noseOuterR) / iod,                     // alae width / IOD
    dist(nasion, subnasale) / faceHeight,                   // nose length / face height
    dist(nasion, subnasale) / iod,                          // nose length / IOD
    dist(mouthR, mouthL) / faceWidth,                       // mouth width / face width
    dist(mouthR, mouthL) / iod,                             // mouth width / IOD
    dist(upperLipTop, lowerLipBottom) / dist(mouthR, mouthL), // lip thickness / mouth width
    dist(jawL, jawR) / faceWidth,                           // jaw span / face width
    dist(gonionL, gonionR) / faceWidth,                     // gonion (angle of jaw) span
    dist(cheekL, cheekR) / faceWidth,                       // cheekbone span share (≈1 by def)
    dist(stomion, chin) / faceHeight,                       // chin height
    dist(eyeMid, chin) / faceHeight,                        // lower-face share
    dist(browMid, eyeMid) / iod,                            // brow-to-eye distance / IOD
    dist(eyeMid, subnasale) / iod,                          // eye-to-nose-base / IOD
    dist(eyeMid, stomion) / iod,                            // eye-to-mouth / IOD
    faceHeight / faceWidth,                                  // overall face aspect ratio
    rEyeHeight / rEyeWidth,                                  // right eye aspect
    lEyeHeight / lEyeWidth,                                  // left eye aspect
    symScore,                                                // facial symmetry (0=perfect)
    jawAngleAvg,                                             // jaw angle (radians)
    (browL.y - browR.y) / iod,                               // brow tilt (asymmetry)
  ];

  // Replace any NaN/Infinity with 0 so we never poison the vector store.
  return feats.map((v) => (isFinite(v) ? Number(v.toFixed(5)) : 0));
}

/** Sample the image at a downscaled resolution to estimate average brightness. */
function estimateBrightness(img) {
  try {
    const canvas = document.createElement("canvas");
    const W = 80, H = 80;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data;
    let total = 0;
    for (let i = 0; i < data.length; i += 4) {
      // Rec. 601 luma
      total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return total / (W * H * 255); // 0..1
  } catch {
    return 0.5;
  }
}

/**
 * Full selfie validation pipeline.
 * Returns:
 *   { ok: true, embedding: number[128], quality_score: 0..1, brightness, face_area_pct, confidence }
 *   { ok: false, reason: string, message: string }
 */
export async function validateSelfie(imgEl) {
  if (!imgEl || !imgEl.complete) {
    return { ok: false, reason: "decode_failed", message: reasonText("decode_failed") };
  }
  const W = imgEl.naturalWidth || imgEl.width;
  const H = imgEl.naturalHeight || imgEl.height;
  if (W < 240 || H < 240) {
    return { ok: false, reason: "too_small_image", message: reasonText("too_small_image") };
  }

  await loadFaceModels();

  // Detect ALL faces so we can reject group selfies.
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.35 });
  const allDetections = await faceapi.detectAllFaces(imgEl, opts);
  if (!allDetections || allDetections.length === 0) {
    return { ok: false, reason: "no_face", message: reasonText("no_face") };
  }
  if (allDetections.length > 1) {
    return { ok: false, reason: "multiple_faces", message: reasonText("multiple_faces") };
  }

  // Detailed pass with landmarks + descriptor.
  const detection = await faceapi
    .detectSingleFace(imgEl, opts)
    .withFaceLandmarks()
    .withFaceDescriptor();
  if (!detection) {
    return { ok: false, reason: "no_face", message: reasonText("no_face") };
  }
  const conf = detection.detection.score;
  const box = detection.detection.box;
  const faceArea = (box.width * box.height) / (W * H);

  if (faceArea < 0.04) {
    return { ok: false, reason: "tiny_face", message: reasonText("tiny_face") };
  }
  if (conf < 0.5) {
    return { ok: false, reason: "low_confidence", message: reasonText("low_confidence") };
  }
  const brightness = estimateBrightness(imgEl);
  if (brightness < 0.18) {
    return { ok: false, reason: "too_dark", message: reasonText("too_dark") };
  }

  // Quality score combines: confidence, face area sweet-spot (10–40%), brightness sweet-spot (~0.5)
  const areaScore = 1 - Math.min(1, Math.abs(faceArea - 0.22) / 0.22);
  const brightnessScore = 1 - Math.min(1, Math.abs(brightness - 0.55) / 0.55);
  const quality = Math.max(
    0,
    Math.min(1, 0.5 * conf + 0.25 * areaScore + 0.25 * brightnessScore),
  );

  return {
    ok: true,
    embedding: Array.from(detection.descriptor),
    geometry: computeGeometry(detection.landmarks),
    geometry_version: GEOMETRY_VERSION,
    quality_score: Number(quality.toFixed(3)),
    brightness: Number(brightness.toFixed(3)),
    face_area_pct: Number(faceArea.toFixed(3)),
    confidence: Number(conf.toFixed(3)),
    model_version: MODEL_VERSION,
  };
}

// Legacy export retained so callers that only need a descriptor still work.
export async function getFaceEmbedding(imgEl) {
  const v = await validateSelfie(imgEl);
  if (!v.ok) return { ok: false, reason: v.reason, message: v.message };
  return { ok: true, embedding: v.embedding, geometry: v.geometry, quality_score: v.quality_score };
}
